import React from "react";
import DecorComboboxBase, { IDecorOption } from "./DecorComboboxBase";

/**
 * BOARD-specifikus decor selector. A régi közös DecorSelectorInput
 * autoSelectFallback={true} ágának VÁLTOZATLAN viselkedése:
 * ha a value nincs az options-ben (pl. worktop-szűrő kizárta), az első
 * opcióra esik vissza és onChange-dzsel felküldi. Boardnál nincs
 * compatible-csoportosítás (eddig sem volt).
 */
interface IBoardDecorSelectorInputProps {
    value?: string;
    options: IDecorOption[];
    tooltip?: string;
    editable?: boolean;
    readOnly?: boolean;
    notFound?: boolean;
    onChange: (newId: string | null) => void;
}

const BoardDecorSelectorInput: React.FC<IBoardDecorSelectorInputProps> = (props) => {
    const [selectedId, setSelectedId] = React.useState<string | null>(props.value || null);
    const [isFocused, setIsFocused] = React.useState(false);

    // Sync, ha a value kívülről változott (paramValue frissítés).
    React.useEffect(() => {
        setSelectedId(props.value || null);
    }, [props.value]);

    const selectedOption = React.useMemo(() => {
        return props.options.find(o => o.id === selectedId) ?? null;
    }, [selectedId, props.options]);

    // Auto-fallback (az eredeti DecorSelectorInput logika, autoSelectFallback=true ág):
    // a lastFallbackRef védi a végtelen loopot, amíg a szülő nem frissítette a value-t.
    const lastFallbackRef = React.useRef<string | null>(null);
    React.useEffect(() => {
        if (selectedOption !== null) {
            lastFallbackRef.current = null;
            return;
        }

        if (props.options.length === 0) return;

        // Szerkesztés közben (fókusz az inputban) nem fallback-elünk: hagyjuk a
        // felhasználót törölni / keresni. Kikattintáskor (blur) az effect újrafut.
        if (isFocused) return;

        const fallbackId = props.options[0].id;
        if (lastFallbackRef.current === fallbackId) return;

        lastFallbackRef.current = fallbackId;
        setSelectedId(fallbackId);
        props.onChange(fallbackId);
    }, [selectedOption, props.options, props.value, isFocused]);

    return (
        <DecorComboboxBase
            selectedOption={selectedOption}
            options={props.options}
            tooltip={props.tooltip}
            editable={props.editable}
            readOnly={props.readOnly}
            notFound={props.notFound}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            onChange={(newId) => {
                setSelectedId(newId);
                props.onChange(newId);
            }}
        />
    );
};

export default BoardDecorSelectorInput;